import { lazy, ComponentType } from 'react';

/**
 * Helper function wrapping React.lazy() to handle stale chunk load failures.
 * When a new deployment changes JS chunk hashes on the web server, older browser sessions
 * fetching old chunk URLs get 404 text/html responses (ChunkLoadError / MIME type error).
 * safeLazy catches chunk errors and reloads the window once to load the latest build assets.
 */
function safeLazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const page = await factory();
      sessionStorage.removeItem('chunk_reload_attempts');
      return page;
    } catch (error: any) {
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        /Loading chunk/i.test(error?.message || '') ||
        /MIME type/i.test(error?.message || '') ||
        /Failed to fetch dynamically imported module/i.test(error?.message || '');

      const hasReloaded = sessionStorage.getItem('chunk_reload_attempts');

      if (isChunkError && !hasReloaded) {
        sessionStorage.setItem('chunk_reload_attempts', '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }

      sessionStorage.removeItem('chunk_reload_attempts');
      throw error;
    }
  });
}

/** Route-level code splitting — each page loads safely when navigated to */
export const AuthCallback = safeLazy(() => import('../../features/auth/pages/AuthCallback'));
export const LoginPage = safeLazy(() => import('../../features/auth/pages/LoginPage'));
export const RegistrationPage = safeLazy(() => import('../../features/auth/pages/RegistrationPage'));
export const ForgotPasswordPage = safeLazy(() => import('../../features/auth/pages/ForgotPasswordPage'));
export const EmailVerificationPage = safeLazy(() => import('../../features/auth/pages/EmailVerificationPage'));
export const GoogleOAuthSuccess = safeLazy(() => import('../../features/auth/pages/GoogleOAuthSuccess'));
export const PendingVerification = safeLazy(() => import('../../features/auth/pages/PendingVerification'));
export const OnboardingPage = safeLazy(() => import('../../features/onboarding/pages/OnboardingPage'));
export const ReferralDashboard = safeLazy(() => import('../../features/referrer/pages/referralDashboard'));
export const JobseekerDashboard = safeLazy(() => import('../../features/job-seeker/pages/jobseekerDashboard'));
export const Support = safeLazy(() => import('../../features/support/pages/Support'));
export const Profile = safeLazy(() => import('../../features/profile/pages/ProfilePage'));
export const Services = safeLazy(() => import('../../features/referrer/pages/Services'));
export const ResumeReview = safeLazy(() => import('../../features/job-seeker/pages/ResumeReview'));
export const CultureCall = safeLazy(() => import('../../features/job-seeker/pages/CultureCall'));
export const ReferralSession = safeLazy(() => import('../../features/job-seeker/pages/ReferralSession'));
export const BookingConfirmed = safeLazy(() => import('../../features/bookings/pages/BookingConfirmed'));
export const MySessions = safeLazy(() => import('../../features/bookings/pages/MySessions'));
export const DailyCall = safeLazy(() => import('../../features/bookings/pages/DailyCall'));
export const RateReview = safeLazy(() => import('../../features/bookings/pages/RateReview'));
export const Rewards = safeLazy(() => import('../../features/misc/pages/Rewards'));
export const Categories = safeLazy(() => import('../../features/misc/pages/Categories'));
export const Notifications = safeLazy(() => import('../../features/misc/pages/Notifications'));
export const JobDetails = safeLazy(() => import('../../features/misc/pages/JobDetails'));
export const DashbordAnalytics = safeLazy(() => import('../../features/referrer/pages/DashbordAnalytics'));
export const JobOpenings = safeLazy(() => import('../../features/job-seeker/pages/JobOpenings'));
export const JobDetailView = safeLazy(() => import('../../features/job-seeker/pages/JobDetailView'));
export const Calender = safeLazy(() => import('../../features/referrer/pages/Calender'));
export const Setting = safeLazy(() => import('../../features/settings/pages/setting'));
export const Invoices = safeLazy(() => import('../../features/bookings/pages/Invoices'));
export const PostJob = safeLazy(() => import('../../features/referrer/pages/PostJob'));
export const ReferrerResumeReview = safeLazy(() => import('../../features/referrer/pages/ReferrerResumeReview'));
export const PayoutTransfer = safeLazy(() => import('../../features/referrer/pages/PayoutTransfer'));
export const Payouts = safeLazy(() => import('../../features/referrer/pages/Payouts'));
export const AdminReferrerUpgrades = safeLazy(() => import('../../features/admin/pages/AdminReferrerUpgrades'));
export const AdminUserDetail = safeLazy(() => import('../../features/admin/pages/AdminUserDetail'));
export const ResumeGrader = safeLazy(() => import('../../features/job-seeker/pages/ResumeGrader'));
export const WeeklyDropsPage = safeLazy(() => import('../../features/job-seeker/pages/WeeklyDropsPage'));
export const ReferralTracker = safeLazy(() => import('../../features/job-seeker/pages/ReferralTracker'));
export const ReferrerDropHubPage = safeLazy(() => import('../../features/referrer/pages/ReferrerDropHubPage'));

// ── Career Trust Layer (Slices A–C) ──────────────────────────────
export const CareerProofPage = safeLazy(() => import('../../features/career-trust/candidate/pages/CareerProofPage'));
export const ClaimDetailPage = safeLazy(() => import('../../features/career-trust/candidate/pages/ClaimDetailPage'));
export const PassportsPage = safeLazy(() => import('../../features/career-trust/candidate/pages/PassportsPage'));
export const PassportViewerPage = safeLazy(() => import('../../features/career-trust/shared/pages/PassportViewerPage'));
export const VerificationRequestsPage = safeLazy(() => import('../../features/career-trust/professional/pages/VerificationRequestsPage'));
